/**
 * /try StoryStream Detection Helpers.
 *
 * WHAT THIS FILE DOES:
 * - Provides minimal heuristics for StoryStream messages delivered over WebSocket.
 * - Detects the first bootstrap frame so /try can navigate on first token.
 * - Detects the first renderable slide so /try can mount the unified reader at TTFS.
 * - Detects a terminal "original" layer frame so /try can avoid unnecessary fallback polling.
 *
 * WHY THIS EXISTS:
 * - We want the /try UI controller to stay focused on orchestration.
 * - The backend can emit bootstrap/terminal/placeholder frames that should not
 *   trigger the "open full reader" transition.
 * - Keeping these rules in one place makes them easy to audit and keep aligned
 *   with extension streaming behavior.
 *
 * CALLED BY:
 * - src/client/try-ui.ts (WebSocket streaming path)
 *
 * @file src/client/try-stream-detect.ts
 */

export type TryStoryStreamMessage = Record<string, unknown>;

/**
 * Placeholder marker inserted by the backend for pending slides.
 */
export const TRY_PLACEHOLDER_MARKER = 'pending-status';

/**
 * Extracts all text-bearing fields from a slide content object.
 *
 * Collects headline, text, html, and bullet strings into a flat array
 * for renderability checks. Returns an empty array when content is missing
 * or not an object.
 *
 * @param content - Slide content payload (typically from StoryStream message).
 * @returns Array of candidate text strings found on the slide.
 */
function extractSlideTextCandidates(content: unknown): string[] {
  if (!content || typeof content !== 'object') {
    return [];
  }

  const record = content as Record<string, unknown>;
  const candidates: string[] = [];

  if (typeof record.headline === 'string') {
    candidates.push(record.headline);
  }
  if (typeof record.text === 'string') {
    candidates.push(record.text);
  }
  if (typeof record.html === 'string') {
    candidates.push(record.html);
  }
  if (typeof record.format === 'string') {
    candidates.push(record.format);
  }
  if (Array.isArray(record.bullets)) {
    for (const bullet of record.bullets) {
      if (typeof bullet === 'string') {
        candidates.push(bullet);
      }
    }
  }

  return candidates;
}

/**
 * Returns true when a slide content object has at least one non-empty, non-placeholder text field.
 *
 * A slide is renderable when:
 * 1. It has at least one text candidate (headline, text, html, or bullet).
 * 2. At least one candidate has non-whitespace content.
 * 3. No candidate contains the TRY_PLACEHOLDER_MARKER (pending-status).
 *
 * @param content - Slide content payload to evaluate.
 * @returns True if the slide has real renderable text.
 */
function isRenderableSlideContent(content: unknown): boolean {
  const candidates = extractSlideTextCandidates(content);
  if (candidates.length === 0) {
    return false;
  }

  const hasRenderableText = candidates.some(value => value.trim().length > 0);
  if (!hasRenderableText) {
    return false;
  }

  return !candidates.some(value => value.includes(TRY_PLACEHOLDER_MARKER));
}

/**
 * Returns true when a `layer_slide` message is renderable and NOT from the "original" layer.
 *
 * The "original" layer contains raw source content (not a gist), so we skip it
 * when deciding whether to open the reader UI.
 *
 * @param message - StoryStream WebSocket message.
 * @returns True if the message is a renderable non-original layer slide.
 */
function isRenderableLayerSlide(message: TryStoryStreamMessage): boolean {
  const type = typeof message.type === 'string' ? message.type : '';
  if (type !== 'layer_slide') {
    return false;
  }

  const layer = typeof message.layer === 'string' ? message.layer.trim() : '';
  if (layer === 'original') {
    return false;
  }

  return isRenderableSlideContent(message.content);
}

/**
 * Returns true when a `story_bootstrap` message carries a renderable initial slide.
 *
 * Bootstrap messages may include an `initialSlide` for instant first-paint. This
 * checks that the slide exists, is not from the "original" layer, and has real
 * renderable text content.
 *
 * @param message - StoryStream WebSocket message.
 * @returns True if the bootstrap includes a renderable non-original initial slide.
 */
function isRenderableInitialSlide(message: TryStoryStreamMessage): boolean {
  const type = typeof message.type === 'string' ? message.type : '';
  if (type !== 'story_bootstrap') {
    return false;
  }

  const candidate = message.initialSlide;
  if (!candidate || typeof candidate !== 'object') {
    return false;
  }

  const slide = candidate as Record<string, unknown>;
  const layer = typeof slide.layer === 'string' ? slide.layer.trim() : '';
  if (layer === 'original') {
    return false;
  }

  const content = typeof slide.content === 'object' && slide.content !== null
    ? slide.content
    : slide;

  return isRenderableSlideContent(content);
}

/**
 * Returns true when a StoryStream message is the bootstrap frame.
 *
 * Bootstrap frames arrive before any renderable slides and indicate
 * the backend has started streaming for the content.
 */
export function isTryStreamBootstrapMessage(message: TryStoryStreamMessage): boolean {
  return typeof message?.type === 'string' && message.type === 'story_bootstrap';
}

/**
 * Returns true when a StoryStream message should trigger the "open reader" transition.
 */
export function shouldOpenTryReaderForMessage(message: TryStoryStreamMessage): boolean {
  return isRenderableLayerSlide(message) || isRenderableInitialSlide(message);
}

/**
 * Returns true when a StoryStream message should trigger navigation to the share page.
 */
export function shouldNavigateOnBootstrap(message: TryStoryStreamMessage): boolean {
  return isTryStreamBootstrapMessage(message);
}

/**
 * Returns true when a StoryStream message represents a terminal original-layer frame.
 *
 * The streaming gateway uses this as a safe marker to close sockets cleanly.
 */
export function isTryStreamTerminalOriginalFrame(message: TryStoryStreamMessage): boolean {
  const type = typeof message.type === 'string' ? message.type : '';
  if (type !== 'layer_complete' && type !== 'layer_failed') {
    return false;
  }

  return typeof message.layer === 'string' && message.layer.trim() === 'original';
}
